import { FieldProps, CheckboxFieldSchema } from '../types/Form';
import { FieldWrapper } from './FieldWrapper';
import { Checkbox } from './Checkbox';
import { Stack } from '@mui/material';
import { TimeRangeField } from './TimeRangeField';
import { Button } from 'system/atoms';

const HOUR_MS = 60 * 60 * 1000;
const WORKDAY_START = new Date('2022-01-01T09:00:00');
const WORKDAY_END = new Date('2022-01-01T17:00:00');
const DAY_END = new Date('2022-01-01T23:59:00');

export const TimeSlots: React.FC<FieldProps<CheckboxFieldSchema>> = ({
  name,
  value = [],
  field,
  errorMessage,
  onChange
}) => {
  const { label, subLabel, containerSx, checkboxLabel } = field;

  const removeItem = (index: number) => {
    const newValue = [...value];
    newValue.splice(index, 1);
    onChange(newValue);
  };

  const addItem = () => {
    if (value.length === 0) {
      onChange([[WORKDAY_START, WORKDAY_END]]);
      return;
    }

    // Repeating the working-day default would overlap what is already there.
    const latestEnd = value.reduce((latest: Date, range: (Date | null)[]) => {
      const end = range?.[1];
      return end instanceof Date && end > latest ? end : latest;
    }, WORKDAY_START);

    const end = new Date(
      Math.min(latestEnd.getTime() + HOUR_MS, DAY_END.getTime())
    );

    onChange([...value, [latestEnd, end]]);
  };

  const handleToggle = () => {
    if (value.length > 0) {
      onChange([]);
    } else {
      addItem();
    }
  };

  return (
    <FieldWrapper
      dataTestId={`time-slots-${name}`}
      label={label}
      subLabel={subLabel}
      errorMessage={errorMessage}
      sx={containerSx}
    >
      <Stack
        data-testid={`time-slots-group-${name}`}
        gap="10px"
        sx={{
          // Gutter is reserved even when the day is off, so toggling one doesn't
          // shunt the row sideways.
          borderLeft: (theme) =>
            value.length > 0
              ? theme.palette.border.large.primary.main
              : '3px solid transparent',
          pl: '6px'
        }}
      >
        <Checkbox
          value={value.length > 0}
          onChange={handleToggle}
          field={{
            checkboxLabel
          }}
        />
        {value.length !== 0 && (
          <>
            {value.map((slot: Date[], index: number) => (
              <Stack
                key={index}
                direction="row"
                alignItems="flex-end"
                gap="30px"
                sx={{
                  ml: '22px'
                }}
              >
                <TimeRangeField
                  name={`time-range-${name}-${index}`}
                  value={slot}
                  field={{
                    containerSx: {
                      width: '300px'
                    },
                    label: 'From (hh:mm) – to (hh:mm)'
                  }}
                  onChange={(newString) => {
                    const newValue = [...value];
                    newValue[index] = newString;
                    onChange(newValue);
                  }}
                />
                {index === 0 && (
                  <Button variant="tertiary" onClick={() => addItem()}>
                    Add hours
                  </Button>
                )}
                {index !== 0 && (
                  <Button
                    variant="tertiary"
                    state="destructive"
                    onClick={() => removeItem(index)}
                  >
                    Delete
                  </Button>
                )}
              </Stack>
            ))}
          </>
        )}
      </Stack>
    </FieldWrapper>
  );
};
